import { EditIcon } from '@/assets/icons/edit';
import { ArrowUp } from '@/assets/static/icons/todo-page/arrow-up';
import { useResponsive } from '@/hooks/use-responsive';
import { themeVar } from '@/styles/theme';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

interface AccordionProps {
  title: React.ReactNode | string;
  children: React.ReactNode;
  collapsable?: boolean;
  openAccordion?: boolean;
  id?: string;
  customActionButtons?: React.ReactNode;
  onEdit?: () => void;
  saveAccordionState?: (id: string, state: { isAccordionOpen: boolean }) => void;
}

export const Accordion = ({
  title,
  children,
  collapsable = false,
  openAccordion = true,
  id,
  customActionButtons,
  onEdit,
  saveAccordionState
}: AccordionProps) => {
  const { isMobile } = useResponsive();

  const [isAccordionOpen, setIsAccordionOpen] = useState(true);

  useEffect(() => {
    setIsAccordionOpen(openAccordion);
  }, [openAccordion]);

  const handleCollapse = () => {
    if (!collapsable) return;
    setIsAccordionOpen(!isAccordionOpen);
    if (saveAccordionState) {
      saveAccordionState(id, { isAccordionOpen: !isAccordionOpen });
    }
  };

  return (
    <AccordionWrapper id={id}>
      <AccordionHeader collapsable={collapsable} onClick={handleCollapse}>
        <AccordionTitle>{title}</AccordionTitle>
        <AccordionActionButtons>
          {onEdit && (
            <AccordionActionButtonItem
              id="accordionActionButton"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              isMobile={isMobile}
            >
              <EditIcon fill={themeVar('font.action')} />
            </AccordionActionButtonItem>
          )}
          {customActionButtons}
          {collapsable && (
            <CollapseButtonWrapper>
              <ArrowUp fill={themeVar('font.action')} style={{ transform: isAccordionOpen ? 'rotate(0deg)' : 'rotate(180deg)' }} />
            </CollapseButtonWrapper>
          )}
        </AccordionActionButtons>
      </AccordionHeader>
      <AccordionBodyWrapper isAccordionOpen={isAccordionOpen}>
        <ExpandableWrapper isMobile={isMobile} isAccordionOpen={isAccordionOpen}>
          {children}
        </ExpandableWrapper>
      </AccordionBodyWrapper>
    </AccordionWrapper>
  );
};

const AccordionWrapper = styled.div`
  background-color: ${themeVar('layer.base')};
  border: 1px solid ${themeVar('border.base')};
  border-radius: 10px;

  @media (hover: hover) {
    &:hover #accordionActionButton {
      opacity: 1;
    }
  }
`;

const AccordionHeader = styled.div<{ collapsable: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
  padding: 20px 30px;
  cursor: ${({ collapsable }) => (collapsable ? 'pointer' : 'default')};
`;

const AccordionTitle = styled.span`
  font-weight: 700;
  font-size: 28px;
  line-height: 22.4px;
  color: ${themeVar('font.strong')};
  padding: 10px 0 5px 0;
  margin-left: 10px;
`;

const AccordionActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

export const AccordionActionButtonItem = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 5px;
  transition: opacity 0.2s;
  opacity: ${({ isMobile }) => (isMobile ? 1 : 0)};
`;

const AccordionBodyWrapper = styled.div<{ isAccordionOpen: boolean }>`
  border-top: ${({ isAccordionOpen }) => (isAccordionOpen ? `1px solid ${themeVar('border.base')}` : 'none')};
  border-radius: 0px 0px 10px 10px;
  background-color: ${themeVar('layer.1')};
  display: grid;
  grid-template-rows: ${({ isAccordionOpen }) => (isAccordionOpen ? '1fr' : '0fr')};
  overflow: ${({ isAccordionOpen }) => (isAccordionOpen ? 'visible' : 'hidden')};
  transition: grid-template-rows 300ms;
`;

const ExpandableWrapper = styled.div<{ isMobile: boolean; isAccordionOpen: boolean }>`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: 20px;
  min-height: 0;
  opacity: ${({ isAccordionOpen }) => (isAccordionOpen ? '1' : '0')};
  padding: ${({ isMobile, isAccordionOpen }) => (isMobile ? '20px' : isAccordionOpen ? '40px' : 0)};
`;

const CollapseButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
`;
